import {I18n} from 'aws-amplify/utils';
import {
    Alert,
    Badge,
    Button,
    Code,
    Group,
    List,
    Modal,
    Stack,
    Text,
} from '@mantine/core';

import {
    Certificate,
    DeleteCertificateResult,
} from '../types';
// FNP-1280 — the shared classifier directly; the certificate-specific
// wrapper that string-matched the backend's refusal prose is deleted, because
// the backend now sends the kind in the message prefix.
import {classifyWriteError} from '../../zones/services/writeErrors';
import {
    certificateWriteErrorCopy,
    deleteRefusedCopy,
} from '../writeErrorMessages';

// FNP-1193 — THE DELETE CONFIRMATION, AND THE PROVIDER'S REFUSAL.
//
// ⚠️ A CONTROLLED Mantine `Modal`, NOT `modals.openConfirmModal`.
//
// @mantine/modals is a dependency but NO `ModalsProvider` is mounted in
// src/App.tsx, so `modals.openConfirmModal` silently does nothing — the worst
// possible failure for a destructive-action guard, because the button would
// appear inert rather than broken. The zones and records modules reached the
// same conclusion for the same reason.
//
// ⚠️ THIS MODAL HAS THREE STATES, NOT TWO. Confirm, refused, and failed.
//
// "Refused" is the one that does not exist elsewhere in this app: the provider
// was reached, it answered, and its answer was no. That is not an error — it is
// the certificate doing its job — and rendering it as a generic failure would
// lose both the reason and the fact that nothing is broken.
export const DeleteCertificateModal = (
    {
        certificate,
        opened,
        onClose,
        onConfirm,
        isDeleting = false,
        refusal,
        error,
    }: {
        certificate?: Certificate,
        opened: boolean,
        onClose: () => void,
        onConfirm: () => void,
        isDeleting?: boolean,
        // ⚠️ THE PROVIDER'S "NO", as a resolved result. Never an exception.
        refusal?: DeleteCertificateResult,
        // A genuine transport or authorisation failure — distinct from a
        // refusal, because one means the provider spoke and the other means it
        // was never reached.
        error?: unknown,
    },
) => {

    if (!certificate) {
        return null;
    }

    const writeError = error ? classifyWriteError(error) : undefined;
    const covered = certificate.coveredNames ?? [];

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={I18n.get('Delete certificate')}
            size={'lg'}
            centered={true}
        >
            <Stack gap={'md'}>

                <Stack gap={4}>
                    <Text size={'sm'}>
                        {I18n.get('You are about to delete this certificate:')}
                    </Text>
                    <Group gap={'xs'}>
                        <Code>{certificate.domainName}</Code>
                        <Badge variant={'light'}>{certificate.region}</Badge>
                    </Group>
                    {/* ⚠️ NAMES EVERYTHING IT COVERS. "Delete 1 item?" is a
                        rubber stamp — the operator already knows they clicked
                        delete. What they may not know is that this certificate
                        also covers three other hostnames that are serving
                        traffic right now. */}
                    {covered.length > 1 && (
                        <Stack gap={2}>
                            <Text size={'xs'} c={'dimmed'}>
                                {I18n.get('This certificate covers:')}
                            </Text>
                            <List size={'xs'} c={'dimmed'}>
                                {covered.map(name => (
                                    <List.Item key={name}>{name}</List.Item>
                                ))}
                            </List>
                        </Stack>
                    )}
                </Stack>

                {/* ⚠️ THE IN-USE REFUSAL. Rendered as INFORMATION, not as a
                    failure: colour and wording both say the provider told us
                    something useful and the certificate is unharmed. */}
                {refusal && (
                    <Alert
                        color={refusal.inUse ? 'yellow' : 'red'}
                        variant={'light'}
                        title={deleteRefusedCopy(refusal).title}
                    >
                        <Stack gap={'xs'}>
                            <Text size={'sm'}>
                                {deleteRefusedCopy(refusal).action}
                            </Text>
                            {/* The provider's own words, VERBATIM. This is the
                                sentence the ticket asks to be rendered rather
                                than a generic error. */}
                            {refusal.reason && (
                                <Text size={'xs'} c={'dimmed'}>
                                    {refusal.reason}
                                </Text>
                            )}
                            {/* The local row survives a refusal, and saying so
                                stops an operator wondering whether the console
                                and the provider now disagree. */}
                            {!refusal.localRecordDeleted && (
                                <Text size={'xs'} c={'dimmed'}>
                                    {I18n.get('The certificate is still listed here, because it still exists.')}
                                </Text>
                            )}
                        </Stack>
                    </Alert>
                )}

                {/* A failure to REACH the provider. Different from a refusal
                    and worded so the two are not confusable. */}
                {writeError && (
                    <Alert
                        color={'red'}
                        variant={'light'}
                        title={certificateWriteErrorCopy(writeError.kind).title}
                    >
                        <Stack gap={'xs'}>
                            <Text size={'sm'}>
                                {certificateWriteErrorCopy(writeError.kind).action}
                            </Text>
                            {writeError.providerMessage && (
                                <Text size={'xs'} c={'dimmed'}>
                                    {writeError.providerMessage}
                                </Text>
                            )}
                            <Code>{writeError.kind}</Code>
                        </Stack>
                    </Alert>
                )}

                {/* ⚠️ SAYS THE PROVIDER DECIDES, up front, so a refusal is not a
                    surprise. Flexi does not pre-check against local state:
                    local state goes stale and the cost of being wrong is a site
                    that stops serving. */}
                <Text size={'sm'}>
                    {I18n.get('The provider decides whether this can be deleted. If something is still serving traffic with it, the deletion will be refused and nothing will change. This cannot be undone once it succeeds.')}
                </Text>

                <Group justify={'flex-end'}>
                    <Button variant={'default'} onClick={onClose} disabled={isDeleting}>
                        {/* After a refusal the only sensible action is to leave,
                            so the cancel button says so. */}
                        {refusal ? I18n.get('Close') : I18n.get('Cancel')}
                    </Button>
                    <Button
                        color={'red'}
                        onClick={onConfirm}
                        loading={isDeleting}
                        // ⚠️ DISABLED AFTER AN IN-USE REFUSAL. Retrying the
                        // identical request against a provider that just
                        // explained why it said no is not a recovery, it is a
                        // second identical refusal. The operator has to go and
                        // detach the resource first.
                        disabled={isDeleting || Boolean(refusal?.inUse)}
                    >
                        {I18n.get('Delete certificate')}
                    </Button>
                </Group>

            </Stack>
        </Modal>
    );
};
